import { AppBar, Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Fade, Grid, IconButton, InputBase, LinearProgress, List, ListItem, ListItemAvatar, ListItemText, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Theme, ThemeProvider, Toolbar, Tooltip, Typography, createStyles, makeStyles, useTheme } from '@material-ui/core';
import { Add, ArrowBack, Assignment, CheckCircle, Delete, Description, Search, Warning } from '@material-ui/icons';
import { Pagination } from '@material-ui/lab';
import color from 'color';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { formatCurrency } from '../../components/CurrencyInput/helpers/CurrencyHelpers';
import Logger from '../../components/Logger';
import ResumoDrawer from '../../components/ResumoDrawer';
import Page from '../../containers/Page/Page';
import { usePermissions } from '../../hooks/permissions';
import { api } from '../../services/api';
import { ContextualTheme } from '../../themes';
import { ILog } from '../../types/Logger';
import { IResumo } from '../../types/Resumo';
import { Helpers } from '../../utils/Helpers';

interface IAcessorio {
    id: string;
    nome: string;
    dummy: boolean;
    preco: {
        lista_descadc: string;
        lista_descforn: string;
        lista_fatdesc: string;
        lista_preco: string;
        lista_precoliq: string;
        terceiros_bruto: string;
        terceiros_descforn: string;
        terceiros_fatimp: string;
        terceiros_fatrev: string;
        terceiros_descseg: string;
        terceiros_icmsret: string;
        terceiros_piscofinsret: string;
        terceiros_precoun: string;
        terceiros_liquido: string;
    }
    pivot: {
        id: string;
        tipo_preco: string;
        qtd: string;
        status: string;
    }
}

interface IProposta {
    lin_sap: string;
    qtd: string;
    tag: string;
    modelo: string;
    servico: string;
    prazo_entrega: string;
    dummy: boolean;
    permite_alterar: boolean;
}

interface IModelo {
    id: string;
    nome: string;
}

interface IModeloData {
    data: IModelo[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface IParams {
    id: string;
    modelo: string;
}

function PropostasAcessorios() {

    //Hooks
    const history = useHistory();
    const classes = useStyles();
    const theme = useTheme();
    const permissions = usePermissions();
    const params = useParams<IParams>();

    //Alert
    const { enqueueSnackbar: alert } = useSnackbar();

    //States
    const [error, setError] = React.useState<string>('');
    const [loadingCount, setLoadingCount] = React.useState<number>(0);
    const [proposta, setProposta] = React.useState<IProposta>({} as IProposta);
    const [acessorios, setAcessorios] = React.useState<IAcessorio[]>([]);
    const [acessoriosTerceiros, setAcessoriosTerceiros] = React.useState<IAcessorio[]>([]);
    const [modeloDialog, setModeloDialog] = React.useState<boolean>(false);
    const [modeloLoading, setModeloLoading] = React.useState<boolean>(false);
    const [modeloSearch, setModeloSearch] = React.useState<string>('');
    const [modelos, setModelos] = React.useState<IModeloData>();
    const [resumo, setResumo] = React.useState<IResumo>({} as IResumo);
    const [resumoError, setResumoError] = React.useState<string>('');
    const [resumoDrawer, setResumoDrawer] = React.useState<boolean>(false);
    const [deleteLoading, setDeleteLoading] = React.useState<string>('');
    const [logs, setLogs] = React.useState<ILog[]>([]);

    //Constantes
    const loading = loadingCount > 0;

    //Effects
    React.useEffect(() => {
        handleFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchModelos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        handleFetchResumo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Funções
    async function handleFetch() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.get(`propostas/${params.modelo}/acessorios`);
            setProposta(response.data.object);
            setAcessorios(response.data.object.acessorios);
            setAcessoriosTerceiros(response.data.object.acessorios_terceiros);
            setLogs(response.data.object.logs);
            setError('');
        } catch (error: any) {
            setError(Helpers.errorFormat(error));
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleFetchResumo() {
        setLoadingCount(c => c + 1);
        try {
            const response = await api.get(`resumo/${params.modelo}`);
            setResumo(response.data.object);
            setResumoError('');
        } catch (error: any) {
            setResumoError(Helpers.errorFormat(error));
        } finally {
            setLoadingCount(c => c - 1);
        }
    }

    async function handleFetchModelos(page: number = 1) {
        setModeloLoading(true);
        try {
            const response = await api.get(`acessorios/bomba/${params.modelo}?search=${modeloSearch}&page=${page}&ativa=1`);
            setModelos(response.data.object);
        } catch (error) {
            Helpers.error(error, alert);
        }
        setModeloLoading(false);
    }

    function handleModelosKeyUp(event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) {
        if (event.key === 'Enter') {
            handleFetchModelos();
        }
    }

    function handleNavigate(acessorio: IAcessorio) {
        const type = acessorio.dummy ? 'dummy' : 'acessorios';
        history.push(`/propostas/${params.id}/modelo/${params.modelo}/${type}/${acessorio.pivot.id}`);
    }

    async function handleDelete(acessorio: IAcessorio, type: string) {
        if (!deleteLoading) {
            setDeleteLoading(acessorio.pivot.id);
            try {
                const response = await api.delete(`propostas/excluir/${params.modelo}/${acessorio.pivot.id}`);
                let newAcessorios = type === 'KSB'
                    ? acessorios.filter(a => a.pivot.id !== acessorio.pivot.id)
                    : acessoriosTerceiros.filter(a => a.pivot.id !== acessorio.pivot.id);
                if (type === 'KSB') setAcessorios(newAcessorios);
                if (type === 'Terceiros') setAcessoriosTerceiros(newAcessorios);
                alert(response.data.message, { variant: 'success' });
            } catch (error) {
                Helpers.error(error, alert);
            }
            setDeleteLoading('');
        }
    }

    async function selectModelo(modelo: IModelo) {
        setModeloLoading(true);
        try {
            const response = await api.post(`propostas/${params.modelo}/acessorio`, {
                modelo_id: modelo.id
            });
            history.push(`/propostas/${params.id}/modelo/${params.modelo}/${response.data.dummy ? 'dummy' : 'acessorios'}/${response.data.object}`);
        } catch (error) {
            Helpers.error(error, alert);
        }
        setModeloLoading(false);
    }

    return (
        <Page error={error} loading={loading}>
            <Page.Title>
                <Box display="flex" flexDirection="row" alignItems="center">
                    <IconButton size="small" color="primary" className={classes.backButton} onClick={() => history.push(`/propostas/${params.id}/${proposta.dummy ? 'dummy' : 'modelo'}/${params.modelo}`)}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        Acessórios
                    </Typography>
                </Box>
                <Box display="flex">
                    <Box mr={2}>
                        <Button
                            color="default"
                            variant="contained"
                            startIcon={<Assignment />}
                            onClick={() => setResumoDrawer(true)}>
                            Resumo
                        </Button>
                        <ResumoDrawer
                            data={resumo}
                            open={resumoDrawer}
                            error={resumoError}
                            onClose={() => setResumoDrawer(false)}
                        />
                    </Box>
                    <Box mr={2}>
                        <Logger
                            logs={logs}
                        />
                    </Box>
                    {permissions.has('proposta.storeModeloAcessorio') &&
                        <Box mr={2}>
                            <Button
                                color="secondary"
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setModeloDialog(true)}>
                                Adicionar
                            </Button>
                            <Dialog open={modeloDialog} fullWidth={true} maxWidth="xs">
                                <AppBar style={{ position: 'relative' }}>
                                    <DialogTitle>Adicionar Acessório</DialogTitle>
                                    <Box mx={3} mb={3}>
                                        <Paper className={classes.custom}>
                                            <InputBase
                                                autoFocus
                                                disabled={loading}
                                                className={classes.input}
                                                placeholder="Pesquisa"
                                                onChange={(event) => setModeloSearch(event.target.value)}
                                                onKeyUp={(event) => handleModelosKeyUp(event)}
                                                value={modeloSearch}
                                            />
                                            <IconButton
                                                disabled={loading}
                                                style={{ padding: 10 }}
                                                onClick={() => handleFetchModelos()}
                                            >
                                                <Search />
                                            </IconButton>
                                        </Paper>
                                        <Box mt={2}>
                                            <Grid container justifyContent="center">
                                                <Pagination
                                                    page={modelos?.current_page || 1}
                                                    count={modelos?.last_page || 1}
                                                    onChange={(_event, page) => handleFetchModelos(page)}
                                                    color="secondary"
                                                />
                                            </Grid>
                                        </Box>
                                    </Box>
                                    <Fade in={modeloLoading} timeout={500}>
                                        <LinearProgress color="secondary"></LinearProgress>
                                    </Fade>
                                </AppBar>
                                <DialogContent>
                                    {modelos &&
                                        <List>
                                            {modelos.data && modelos.data.map((modelo, index) => (
                                                <ListItem button key={index} onClick={() => selectModelo(modelo)}>
                                                    <ListItemAvatar>
                                                        <Avatar>
                                                            <Add />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={modelo.nome}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    }
                                </DialogContent>
                                <DialogActions>
                                    <Button color="primary" onClick={() => setModeloDialog(false)}>
                                        Fechar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Box>
                    }
                    <Button
                        color="primary"
                        variant="contained"
                        startIcon={<Description />}
                        onClick={() => history.push(`/propostas/${params.id}`)}>
                        Proposta
                    </Button>
                </Box>
            </Page.Title>
            <Page.Section>
                <Page.Content>
                    <Box p={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Linha SAP"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.lin_sap || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="TAG"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.tag || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Quantidade"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.qtd || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Modelo"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.modelo || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Serviço"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.servico || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Prazo de Entrega"
                                    variant="outlined"
                                    size="small"
                                    fullWidth={true}
                                    disabled={true}
                                    value={proposta.prazo_entrega || ''}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Page.Content>
            </Page.Section>
            <Page.Section>
                <Page.Content>
                    <Toolbar className={classes.toolbar}>
                        <Typography className={classes.title} variant="h6" component="div">
                            Acessórios KSB
                        </Typography>
                    </Toolbar>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="5px">Item</TableCell>
                                    <TableCell align="center" width="5px">Status</TableCell>
                                    <TableCell>Modelo</TableCell>
                                    <TableCell>Qtd.</TableCell>
                                    <TableCell>Preço Un. Bruto Sem IPI</TableCell>
                                    <TableCell>Desc. Adc.</TableCell>
                                    <TableCell>Fator Desc.</TableCell>
                                    <TableCell>Preço Un. Líquido de Venda</TableCell>
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {acessorios && acessorios.map((acessorio, index) =>
                                    <TableRow key={index} hover={true} className={acessorio.pivot.status ? classes.rowWarning : undefined}>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{index + 1}</TableCell>
                                        <TableCell align="center">
                                            {acessorio.pivot.status ?
                                                <Tooltip arrow={true} title={acessorio.pivot.status}>
                                                    <Warning style={{ color: theme.palette.warning.main }} />
                                                </Tooltip>
                                                :
                                                <CheckCircle style={{ color: theme.palette.success.main }} />
                                            }
                                        </TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}><Chip label={acessorio.nome} /></TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.pivot.qtd}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? formatCurrency('pt-BR', parseFloat(acessorio.preco.lista_preco), 'BRL', true) : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? acessorio.preco.lista_descadc : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? acessorio.preco.lista_fatdesc : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? formatCurrency('pt-BR', parseFloat(acessorio.preco.lista_precoliq), 'BRL', true) : ''}</TableCell>
                                        <TableCell align="center">
                                            <ThemeProvider theme={ContextualTheme}>
                                                {deleteLoading === acessorio.pivot.id ?
                                                    <CircularProgress color="secondary" size={16} />
                                                    :
                                                    <IconButton size="small" color="secondary" disabled={!permissions.has('proposta.limpaModeloAcessorio') || !proposta.permite_alterar} onClick={() => handleDelete(acessorio, "KSB")}>
                                                        <Delete />
                                                    </IconButton>
                                                }
                                            </ThemeProvider>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Page.Content>
            </Page.Section>
            <Page.Section>
                <Page.Content>
                    <Toolbar className={classes.toolbar}>
                        <Typography className={classes.title} variant="h6" component="div">
                            Acessórios Terceiros
                        </Typography>
                    </Toolbar>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="5px">Item</TableCell>
                                    <TableCell align="center" width="5px">Status</TableCell>
                                    <TableCell>Modelo</TableCell>
                                    <TableCell>Qtd.</TableCell>
                                    <TableCell>Preço Un. Bruto Sem IPI</TableCell>
                                    <TableCell>Desc. Forn</TableCell>
                                    <TableCell>ICMS à Retirar</TableCell>
                                    <TableCell>PIS/COFINS à Retirar</TableCell>
                                    <TableCell>Preço Un. Líquido de Venda</TableCell>
                                    <TableCell align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {acessoriosTerceiros && acessoriosTerceiros.map((acessorio, index) =>
                                    <TableRow key={index} hover={true} className={acessorio.pivot.status ? classes.rowWarning : undefined}>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{index + 1}</TableCell>
                                        <TableCell align="center">
                                            {acessorio.pivot.status ?
                                                <Tooltip arrow={true} title={acessorio.pivot.status}>
                                                    <Warning style={{ color: theme.palette.warning.main }} />
                                                </Tooltip>
                                                :
                                                <CheckCircle style={{ color: theme.palette.success.main }} />
                                            }
                                        </TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}><Tooltip title={acessorio.dummy ? 'Acessório Genérico' : ''}><Chip label={acessorio.nome} color={acessorio.dummy ? 'primary' : 'default'} /></Tooltip></TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.pivot.qtd}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco && acessorio.preco.terceiros_bruto ? formatCurrency('pt-BR', parseFloat(acessorio.preco.terceiros_bruto), 'BRL', true) : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? acessorio.preco.terceiros_descforn : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? acessorio.preco.terceiros_icmsret : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? acessorio.preco.terceiros_piscofinsret : ''}</TableCell>
                                        <TableCell onClick={() => handleNavigate(acessorio)}>{acessorio.preco ? formatCurrency('pt-BR', parseFloat(acessorio.preco.terceiros_precoun), 'BRL', true) : ''}</TableCell>
                                        <TableCell align="center">
                                            <ThemeProvider theme={ContextualTheme}>
                                                {deleteLoading === acessorio.pivot.id ?
                                                    <CircularProgress color="secondary" size={16} />
                                                    :
                                                    <IconButton size="small" color="secondary" disabled={!permissions.has('proposta.limpaModeloAcessorio') || !proposta.permite_alterar} onClick={() => handleDelete(acessorio, "Terceiros")}>
                                                        <Delete />
                                                    </IconButton>
                                                }
                                            </ThemeProvider>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Page.Content>
            </Page.Section>
        </Page>
    );
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        backButton: {
            marginRight: theme.spacing(1),
        },
        row: {
            marginBottom: theme.spacing(1)
        },
        rowWarning: {
            backgroundColor: color(theme.palette.warning.main).alpha(0.15).rgb().toString(),
            '&:hover': {
                backgroundColor: `${color(theme.palette.warning.main).alpha(0.19).rgb().toString()} !important`,
            },
        },
        custom: {
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
        },
        toolbar: {
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(1),
        },
        title: {
            flex: '1 1 100%',
        },
    })
);

export default PropostasAcessorios;